import { startOfDayUTC } from "~/utils/datetime"
import { WEEKDAYS } from "~/utils/replenishmentRules"
import { weekdayToRruleDay } from "~/utils/rruleWeekdays"

// The one level maths for a replenishment rule, shared by the engine, the rule
// simulation and the grid. Pure.

/** AllocationStore.transferDays' default, used when a rule has no store to read it from. */
export const DEFAULT_TRANSIT_DAYS = 6
/** Seeds the overstock level of a days-mode rule as a multiple of its order-up-to cover. */
export const DEFAULT_OVERSTOCK_FACTOR = 1.5
/** A rule without a cut-off time closes at 12:00 UTC. */
export const DEFAULT_CUTOFF_MINUTES = 12 * 60

export type RuleSchedule = { freq: string; byDay: readonly string[]; interval: number }
export type RuleCover = RuleSchedule & { safetyStockDays: number }
export type RuleLevelsInput = RuleCover & {
  unit: string
  minStock: number
  upToStock: number
  maxStock: number
}
export type LineLevelsInput = {
  ros: number
  transitDays: number
  /** The gap after this run's departure; the rule's average gap when absent. */
  reviewPeriodDays?: number
}
export type RuleLevels = { reorderPoint: number; orderUpTo: number; overstock: number }
export type RuleDepartureSchedule = RuleSchedule & {
  /** "YYYY-MM-DD"; a Date is read as its UTC calendar day. */
  dtstart?: string | Date | null
  /** A time of day: a Date read in UTC (the date part is ignored) or "HH:MM". */
  cutoff?: string | Date | null
}

// ROS x cover lands on float noise (0.07 x 100 = 7.000000000000001), which a
// bare ceil would round up a whole unit.
export const ceilUnits = (x: number) => Math.ceil(Number(x.toFixed(6)))

/**
 * The average days between departures: DAILY is every interval days; WEEKLY
 * spreads 7 x interval over its distinct departure days. Uneven weekdays
 * (Mon and Sun: gaps of 6 and 1) average out here; a run sizes its order on
 * the real gap, reviewPeriodDaysFrom.
 */
export function reviewPeriodDays({ freq, byDay, interval }: RuleSchedule): number {
  const every = Math.max(1, interval)
  if (freq === "DAILY") return every
  return Math.max(1, (7 * every) / Math.max(1, new Set(byDay).size))
}

/** Reorder point cover: transit time + safety stock. */
export function ropCoverDays(rule: Pick<RuleCover, "safetyStockDays">, transitDays: number) {
  return transitDays + rule.safetyStockDays
}

/** Order-up-to cover: transit time + safety stock + review period. */
export function outCoverDays(
  rule: RuleCover,
  transitDays: number,
  reviewDays: number = reviewPeriodDays(rule),
) {
  return ropCoverDays(rule, transitDays) + reviewDays
}

/**
 * Reorder point, order-up-to level and overstock level of one line from the rule
 * and the rate of sale alone. Unit mode reads minStock, upToStock and maxStock as
 * units; day mode derives the first two from the covers and reads maxStock as
 * days of stock. Each level is raised to the one below it. The line's minimum
 * and maximum never move a level: they limit the suggestion (suggestedWithinLimits).
 */
export function ruleLevels(rule: RuleLevelsInput, line: LineLevelsInput): RuleLevels {
  if (rule.unit === "quantity") {
    const orderUpTo = Math.max(rule.upToStock, rule.minStock)
    return {
      reorderPoint: rule.minStock,
      orderUpTo,
      overstock: Math.max(rule.maxStock, orderUpTo),
    }
  }
  const reorderPoint = ceilUnits(line.ros * ropCoverDays(rule, line.transitDays))
  const orderUpTo = Math.max(
    ceilUnits(line.ros * outCoverDays(rule, line.transitDays, line.reviewPeriodDays)),
    reorderPoint,
  )
  return {
    reorderPoint,
    orderUpTo,
    overstock: Math.max(ceilUnits(line.ros * rule.maxStock), orderUpTo),
  }
}

type ArrivalInput = { ros: number; transitDays: number }

/**
 * A line's minimum display quantity (0 for none), maximum stock and order multiple, with the rate
 * of sale and transit days that tell what it sells before a delivery: the minimum is checked at arrival.
 */
export type LineLimits = ArrivalInput & {
  mdq: number
  maxQty: number | null
  orderMultiple: number
}

/** Rate of sale x transit days: what a store sells between the departure and the delivery. */
export const salesBeforeArrival = ({ ros, transitDays }: ArrivalInput) => ros * transitDays

/** The inventory position left when a delivery arrives, before the delivery itself. */
export const positionAtArrival = (position: number, sales: number) => Math.max(0, position - sales)

/** What the store holds when a delivery of quantity arrives. */
export const stockAtArrival = (line: ArrivalInput & { position: number }, quantity: number) =>
  positionAtArrival(line.position, salesBeforeArrival(line)) + quantity

const roundUpTo = (units: number, multiple: number) =>
  ceilUnits(Math.max(0, units) / multiple) * multiple

/** The suggestion before the line's settings: nothing above the reorder point, else the gap to the order-up-to level. */
export function unrestrictedSuggestion(
  lv: Pick<RuleLevels, "reorderPoint" | "orderUpTo">,
  position: number,
): number {
  if (position > lv.reorderPoint) return 0
  return Math.max(0, lv.orderUpTo - position)
}

/**
 * How the maximum lowered a suggestion, null when it did not:
 * - atMaximum: the inventory position already reaches it, so 0
 * - noMultipleFits: there is room, but less than one order multiple, so 0
 * - capped: the units wanted pass it, so the suggestion fills up to it
 * - cappedToMultiple: as capped, but only the largest order multiple that fits
 * - minimumCapped: the need fits, but the raise to the minimum display quantity passes it
 * - rounding: the units wanted fit, but rounding up to the order multiple would pass it
 */
export type MaxLimit =
  | "atMaximum"
  | "noMultipleFits"
  | "capped"
  | "cappedToMultiple"
  | "minimumCapped"
  | "rounding"
  | null

function maxLimitFor(
  wanted: number,
  room: number,
  quantity: number,
  minimumRaised: boolean,
): MaxLimit {
  if (room === 0) return "atMaximum"
  if (quantity === 0) return "noMultipleFits"
  if (wanted <= room) return minimumRaised ? "minimumCapped" : "rounding"
  return quantity === room ? "capped" : "cappedToMultiple"
}

/** The units that bring the stock at arrival up to the minimum display quantity, rounded up to the order multiple. */
export const quantityToMinimum = (position: number, limits: LineLimits) =>
  roundUpTo(
    limits.mdq - stockAtArrival({ ...limits, position }, 0),
    Math.max(1, limits.orderMultiple),
  )

/** A need rounded up to the order multiple and raised to the minimum display quantity, before the maximum cuts it. */
export const quantityBeforeCap = (need: number, position: number, limits: LineLimits) =>
  Math.max(roundUpTo(need, Math.max(1, limits.orderMultiple)), quantityToMinimum(position, limits))

export type LimitedQuantity = { quantity: number; maxLimit: MaxLimit; minimumRaised: boolean }

/**
 * A need rounded up to the order multiple and raised so the stock at arrival reaches the minimum
 * display quantity, then cut to the largest order multiple that keeps the inventory position at or
 * under the maximum stock.
 */
function withinLimits(need: number, position: number, limits: LineLimits): LimitedQuantity {
  const { maxQty, orderMultiple } = limits
  const multiple = Math.max(1, orderMultiple)
  const quantity = quantityBeforeCap(need, position, limits)
  const minimumRaised = quantityToMinimum(position, limits) > roundUpTo(need, multiple)
  if (maxQty == null || quantity === 0 || position + quantity <= maxQty) {
    return { quantity, maxLimit: null, minimumRaised }
  }
  const room = Math.max(0, maxQty - position)
  const capped = Math.floor(room / multiple) * multiple
  return {
    quantity: capped,
    maxLimit: maxLimitFor(need, room, capped, minimumRaised),
    minimumRaised,
  }
}

/** The unrestricted suggestion, limited by the line's minimum display quantity, maximum stock and order multiple. */
export function suggestedWithinLimits(
  lv: Pick<RuleLevels, "reorderPoint" | "orderUpTo">,
  position: number,
  limits: LineLimits,
): LimitedQuantity {
  return withinLimits(unrestrictedSuggestion(lv, position), position, limits)
}

/** The gap to the order-up-to level, limited as the engine limits a suggestion. Ignores the reorder point. */
export function unitsToOrderUpTo(
  orderUpTo: number,
  position: number,
  limits: LineLimits,
): LimitedQuantity {
  return withinLimits(orderUpTo - position, position, limits)
}

/** True when the quantity takes the inventory position above the overstock level. */
export function overstockWarning(
  lv: Pick<RuleLevels, "overstock">,
  position: number,
  quantity: number,
) {
  return quantity > 0 && position + quantity > lv.overstock
}

/** The fastest of a rule's store transit times, or the store default when it has none. */
export function fastestTransitDays(transferDays: readonly number[]): number {
  return transferDays.length === 0 ? DEFAULT_TRANSIT_DAYS : Math.min(...transferDays)
}

/**
 * The "order-up-to level x 1.50" hint of a days-mode rule: its overstock days
 * over its order-up-to cover at the fastest transit time. Null in unit mode.
 */
export function overstockFactorHint(
  rule: RuleCover & Pick<RuleLevelsInput, "unit" | "maxStock">,
  fastestTransit: number,
): number | null {
  if (rule.unit === "quantity") return null
  const cover = outCoverDays(rule, fastestTransit)
  return cover > 0 ? rule.maxStock / cover : null
}

/** The overstock days a new days-mode rule starts with; the migration seeded rules without one the same way. */
export function defaultOverstockDays(rule: RuleCover, fastestTransit: number): number {
  return ceilUnits(DEFAULT_OVERSTOCK_FACTOR * outCoverDays(rule, fastestTransit))
}

const DAY_MS = 86_400_000

const dayNumber = (date: Date) => Math.round(startOfDayUTC(date).getTime() / DAY_MS)
const dateOfDay = (day: number) => new Date(day * DAY_MS)
// Day 0, 1 Jan 1970, is a Thursday: index 3 when Monday is 0.
const mondayIndex = (day: number) => (((day + 3) % 7) + 7) % 7
const mondayOf = (day: number) => day - mondayIndex(day)

function dayNumberOf(value: string | Date): number {
  return typeof value === "string"
    ? dayNumber(new Date(`${value.slice(0, 10)}T00:00:00Z`))
    : dayNumber(value)
}

export function cutoffMinutesUTC(cutoff: string | Date | null | undefined): number {
  if (cutoff == null) return DEFAULT_CUTOFF_MINUTES
  const clock = typeof cutoff === "string" ? /^(\d{1,2}):(\d{2})$/.exec(cutoff) : null
  if (clock) return Number(clock[1]) * 60 + Number(clock[2])
  const date = new Date(cutoff)
  if (Number.isNaN(date.getTime())) return DEFAULT_CUTOFF_MINUTES
  return date.getUTCHours() * 60 + date.getUTCMinutes()
}

/**
 * The next departure day on or after `from` (UTC midnight), when stock leaves
 * the warehouse for the store, or null when a WEEKLY rule has no departure day.
 * From the cut-off time on, today is missed. dtstart anchors which days (DAILY)
 * or weeks (WEEKLY) are on when the interval is above 1; without one the first
 * possible departure starts the count.
 */
export function nextDepartureDate(rule: RuleDepartureSchedule, from: Date): Date | null {
  const minutes = from.getUTCHours() * 60 + from.getUTCMinutes()
  const earliest = dayNumber(from) + (minutes >= cutoffMinutesUTC(rule.cutoff) ? 1 : 0)
  const anchor = rule.dtstart == null ? null : dayNumberOf(rule.dtstart)
  const start = anchor == null ? earliest : Math.max(earliest, anchor)
  const every = Math.max(1, rule.interval)

  if (rule.freq === "DAILY") {
    if (anchor == null) return dateOfDay(start)
    return dateOfDay(anchor + Math.ceil((start - anchor) / every) * every)
  }

  const wanted = new Set(rule.byDay)
  for (let day = start; day < start + 7 * every; day++) {
    if (anchor != null && Math.floor((mondayOf(day) - mondayOf(anchor)) / 7) % every !== 0) continue
    if (wanted.has(weekdayToRruleDay(WEEKDAYS[mondayIndex(day)] ?? ""))) return dateOfDay(day)
  }
  return null
}

/** The last moment a change makes the next departure: that departure day at the cut-off time. */
export function changesDueBy(rule: RuleDepartureSchedule, from: Date): Date | null {
  const departure = nextDepartureDate(rule, from)
  if (departure == null) return null
  return new Date(departure.getTime() + cutoffMinutesUTC(rule.cutoff) * 60_000)
}

/** When the next departure reaches the store: departure day plus transit days. */
export function plannedDeliveryDate(
  rule: RuleDepartureSchedule,
  from: Date,
  transitDays: number,
): Date | null {
  const departure = nextDepartureDate(rule, from)
  if (departure == null) return null
  return new Date(departure.getTime() + transitDays * DAY_MS)
}

/**
 * The departure after `departure`. Without a start date the schedule counts
 * its intervals from `departure`; the cut-off never applies past the first.
 */
export function departureAfter(rule: RuleDepartureSchedule, departure: Date): Date | null {
  const schedule = { ...rule, dtstart: rule.dtstart ?? departure, cutoff: "23:59" }
  return nextDepartureDate(schedule, new Date(departure.getTime() + DAY_MS))
}

/**
 * The days a run's order has to last: from the next departure (the one a
 * change made at `from` still catches) to the departure after it. The average
 * gap when the rule has no departure day.
 */
export function reviewPeriodDaysFrom(rule: RuleDepartureSchedule, from: Date): number {
  const departure = nextDepartureDate(rule, from)
  const following = departure ? departureAfter(rule, departure) : null
  if (departure == null || following == null) return reviewPeriodDays(rule)
  return Math.round((following.getTime() - departure.getTime()) / DAY_MS)
}

/** The shortest and longest gap between departures over one full cycle of the schedule. */
export function reviewPeriodRange(rule: RuleDepartureSchedule): { min: number; max: number } {
  const average = reviewPeriodDays(rule)
  if (rule.freq === "DAILY") return { min: average, max: average }
  const first = nextDepartureDate({ ...rule, cutoff: "23:59" }, CYCLE_REFERENCE_MONDAY)
  if (first == null) return { min: average, max: average }
  const gaps: number[] = []
  let departure = first
  for (let i = 0; i < new Set(rule.byDay).size; i++) {
    const next = departureAfter({ ...rule, dtstart: rule.dtstart ?? first }, departure)
    if (next == null) break
    gaps.push(Math.round((next.getTime() - departure.getTime()) / DAY_MS))
    departure = next
  }
  if (gaps.length === 0) return { min: average, max: average }
  return { min: Math.min(...gaps), max: Math.max(...gaps) }
}

const CYCLE_REFERENCE_MONDAY = new Date(Date.UTC(2026, 0, 5))
